/*
 * Photo Headshot (AI) panel.
 *
 * Select a portrait image on the slide, then pick a design from the gallery —
 * clicking one runs the pipeline (capture -> AI face detection -> crop ->
 * compose) and replaces the selected image with the result. Mirrors the
 * DecKorate POC's chat-based design picker (aiDesigner.js's
 * `_handleCreatePhotoHead`) as a standalone panel instead of a chat bubble.
 */

import { useCallback } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { RunStatusSection } from "../../shared/components/RunStatusSection";
import { SelectionHint } from "../../shared/components/SelectionHint";
import { usePhotoHeadshot } from "../hooks/usePhotoHeadshot";
import { PHOTO_HEAD_DESIGNS } from "../services/photoHeadDesigns";
import { getErrorMessage, logPhotoHeadError } from "../services/photoHeadApi";
import type { PhotoHeadDesign, PhotoHeadshotPhase } from "../../../types";
import shared from "../../shared/styles/panelStatus.module.css";
import styles from "./PhotoHeadshotPanel.module.css";

interface PhotoHeadshotPanelProps {
  readonly onBack?: () => void;
}

const PHASES: ReadonlyArray<{ readonly phase: PhotoHeadshotPhase; readonly label: string }> = [
  { phase: "reading", label: "Reading the selected photo" },
  { phase: "detecting", label: "Detecting the face region (AI)" },
  { phase: "cropping", label: "Cropping to the face" },
  { phase: "composing", label: "Building the photo head" },
];

function reportRunFailure(error: unknown): void {
  logPhotoHeadError("photo-head-run-ui", getErrorMessage(error, "Unexpected error"));
}

function TemplateSwatch({
  design,
  template,
}: Readonly<{ design: PhotoHeadDesign; template: NonNullable<PhotoHeadDesign["template"]> }>) {
  const isTop = template.layout === "top";
  const lineColor = template.darkMode ? "#fff" : "#323130";
  const cardStyle = {
    width: isTop ? 36 : 52,
    height: isTop ? 44 : 28,
    borderRadius: template.cardRadius,
    background: `#${template.cardBg}`,
    flexDirection: isTop ? "column" : "row",
  } as const;
  const avatarStyle = {
    width: isTop ? 14 : 18,
    height: isTop ? 14 : 18,
    border: `1px solid #${design.borderColor ?? "ccc"}`,
  };

  return (
    <div className={styles.swatchCard} style={cardStyle}>
      <div className={styles.swatchAvatar} style={avatarStyle} />
      <div className={styles.swatchLines} style={isTop ? { alignItems: "center" } : undefined}>
        <span style={{ width: isTop ? 20 : 22, background: lineColor }} />
        <span style={{ width: isTop ? 16 : 18, background: lineColor }} />
      </div>
    </div>
  );
}

function plainSwatchRadius(shape: PhotoHeadDesign["shape"]): number | "50%" {
  if (shape === "OVAL") return "50%";
  if (shape === "ROUNDED_RECTANGLE") return 6;
  return 2;
}

function PlainSwatch({ design }: Readonly<{ design: PhotoHeadDesign }>) {
  const className = design.shadow ? `${styles.swatchPlain} ${styles.swatchShadow}` : styles.swatchPlain;
  const style = {
    borderRadius: plainSwatchRadius(design.shape),
    border: design.borderColor ? `${design.borderWidth}px solid #${design.borderColor}` : "none",
  };
  return <div className={className} style={style} />;
}

function DesignSwatch({ design }: Readonly<{ design: PhotoHeadDesign }>) {
  return design.template ? (
    <TemplateSwatch design={design} template={design.template} />
  ) : (
    <PlainSwatch design={design} />
  );
}

function DesignGallery({
  disabled,
  activeDesignId,
  onPick,
}: Readonly<{ disabled: boolean; activeDesignId: string | null; onPick: (design: PhotoHeadDesign) => void }>) {
  return (
    <div className={styles.grid}>
      {PHOTO_HEAD_DESIGNS.map((design) => (
        <button
          key={design.id}
          type="button"
          className={design.id === activeDesignId ? `${styles.designBtn} ${styles.designBtnActive}` : styles.designBtn}
          onClick={() => onPick(design)}
          disabled={disabled}
        >
          <DesignSwatch design={design} />
          <span className={styles.designLabel}>{design.label}</span>
        </button>
      ))}
    </div>
  );
}

export function PhotoHeadshotPanel({ onBack }: Readonly<PhotoHeadshotPanelProps>) {
  const { state, selectedShape, selectionError, run, cancel, reset } = usePhotoHeadshot();
  const isRunning = state.status === "running";
  const activeDesignId = state.status === "running" || state.status === "done" ? state.designId : null;

  const handlePick = useCallback(
    (design: PhotoHeadDesign) => {
      run(design).catch(reportRunFailure);
    },
    [run],
  );

  return (
    <div className={shared.panel}>
      <ModuleHeader title="Photo Headshot" subtitle="Deckorate · AI" onBack={onBack} />
      <div className={shared.body}>
        <p className={shared.desc}>
          Select a portrait image on the slide, then pick a design — AI finds the face, crops to it, and builds the
          photo head in its place.
        </p>

        {!selectedShape && (
          <SelectionHint
            selectionError={selectionError}
            defaultMessage="Select a portrait image on the slide to see design options."
            classNames={shared}
          />
        )}

        {selectedShape && (
          <fieldset className={styles.designGroup}>
            <legend className={styles.label}>Choose a design</legend>
            <DesignGallery disabled={isRunning} activeDesignId={activeDesignId} onPick={handlePick} />
          </fieldset>
        )}

        <RunStatusSection
          status={state.status}
          phase={state.status === "running" ? state.phase : undefined}
          phases={PHASES}
          progressAriaLabel="Photo headshot progress"
          doneMessage="Photo headshot created."
          errorMessage={state.status === "error" ? state.message : undefined}
          onCancel={cancel}
          onDismiss={reset}
          classNames={shared}
        />
      </div>
    </div>
  );
}
